export type HostConsentState = { analytics: boolean };

export const HOST_CONSENT_CHANGE_EVENT = 'eb-web-consent-change';

const AIRGAP_CONSENT_CHANGE_EVENT = 'consent-change';
const AIRGAP_SYNC_EVENT = 'sync';
const AIRGAP_POLL_INTERVAL_MS = 500;
const AIRGAP_POLL_ATTEMPTS = 30;

type TranscendConsentData = {
  purposes?: Record<string, unknown>;
};

type TranscendAirgap = {
  getConsent?: () => TranscendConsentData;
  addEventListener?: (event: string, handler: () => void) => void;
  removeEventListener?: (event: string, handler: () => void) => void;
  ready?: (callback: (airgap?: TranscendAirgap) => void) => void;
};

declare global {
  interface Window {
    airgap?: TranscendAirgap;
    __EB_WEB_CONSENT__?: { analytics?: boolean };
  }
}

/**
 * Reads Transcend's `window.airgap` and fails closed: only an explicit
 * `purposes.Analytics === true` grants host analytics consent. Every other
 * value, including `'Auto'`, is denied, so regime defaults can never turn
 * tracking on for a visitor who made no choice.
 */
export function getHostConsentState(): HostConsentState {
  if (typeof window === 'undefined') {
    return { analytics: false };
  }

  try {
    const consent = window.airgap?.getConsent?.();
    return { analytics: consent?.purposes?.Analytics === true };
  } catch {
    return { analytics: false };
  }
}

export function publishHostConsent(): HostConsentState {
  const state = getHostConsentState();
  if (typeof window === 'undefined') {
    return state;
  }

  const changed = window.__EB_WEB_CONSENT__?.analytics !== state.analytics;
  window.__EB_WEB_CONSENT__ = state;
  if (changed) {
    window.dispatchEvent(new Event(HOST_CONSENT_CHANGE_EVENT));
  }
  return state;
}

/**
 * Publishes the current consent state, then keeps it current: the airgap is
 * parser-blocking but may still be initializing when the host mounts, so this
 * subscribes through `airgap.ready` when the pre-init shim is present and
 * bounded-polls as a fallback, then listens for Transcend's `sync` (server
 * consent load) and `consent-change` (visitor decision) events. Returns an
 * unsubscribe function.
 */
export function subscribeToHostConsent(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  publishHostConsent();

  let disposed = false;
  let attachedTo: TranscendAirgap | undefined;
  let pollId: ReturnType<typeof setInterval> | undefined;
  let readyRequested = false;

  const refresh = () => {
    publishHostConsent();
  };

  const stopPolling = () => {
    if (pollId !== undefined) {
      clearInterval(pollId);
      pollId = undefined;
    }
  };

  const attach = (airgap: TranscendAirgap | undefined): boolean => {
    if (disposed || attachedTo || !airgap || typeof airgap.addEventListener !== 'function') {
      return attachedTo !== undefined;
    }
    airgap.addEventListener(AIRGAP_CONSENT_CHANGE_EVENT, refresh);
    airgap.addEventListener(AIRGAP_SYNC_EVENT, refresh);
    attachedTo = airgap;
    return true;
  };

  const tryAttach = (): boolean => {
    if (attach(window.airgap)) {
      stopPolling();
      refresh();
      return true;
    }
    return false;
  };

  const airgap = window.airgap;
  if (!tryAttach() && !disposed) {
    if (!readyRequested && typeof airgap?.ready === 'function') {
      readyRequested = true;
      airgap.ready((readyAirgap) => {
        if (!disposed && attach(readyAirgap ?? window.airgap)) {
          stopPolling();
          refresh();
        }
      });
    }

    let attempts = 0;
    pollId = setInterval(() => {
      attempts += 1;
      if (tryAttach() || attempts >= AIRGAP_POLL_ATTEMPTS) {
        stopPolling();
      }
    }, AIRGAP_POLL_INTERVAL_MS);
  }

  return () => {
    disposed = true;
    stopPolling();
    if (attachedTo) {
      attachedTo.removeEventListener?.(AIRGAP_CONSENT_CHANGE_EVENT, refresh);
      attachedTo.removeEventListener?.(AIRGAP_SYNC_EVENT, refresh);
      attachedTo = undefined;
    }
  };
}
